"use client";

import Image from "next/image";
import * as m from "motion/react-m";
import { AnimatePresence, type MotionValue } from "motion/react";
import clsx from "clsx";
import { useTranslations } from "next-intl";

import { iconStrokeFix } from "@ui/central-icons/iconUtils";
import IconFeature from "@ui/central-icons/icons/IconFeature";
import { AnimatedDotGrid } from "@ui/components/core/AnimatedDotGrid";
import { RandomFact } from "@ui/components/core/RandomFact";

import { ElevatedButton } from "./ElevatedButton";
import { type InteractiveImageCreationResult, type InteractiveImageGenerationError, makeCrossfade } from "./types";

const FULL_INSET = { top: "0%", left: "0%", right: "0%", bottom: "0%" } as const;
const CARD_CLASS = "tw-absolute tw-overflow-hidden tw-rounded-2.5xl";
const PLACEHOLDER_CARD_CLASS = clsx(CARD_CLASS, "tw-bg-gray-200");

export type CreateState = "idle" | "generating" | "result";

interface CreateModeProps {
  createState: CreateState;
  /** Shared card scale: empty/generating stay inset, a loaded result grows full-frame. */
  cardScale: MotionValue<number>;
  /** Inverse-ish companions that keep placeholder copy readable and image crops lively. */
  contentScale: MotionValue<number>;
  imageScale: MotionValue<number>;
  /** A result has been revealed at least once (drives full-bleed vs inset card). */
  hasResult: boolean;
  /** The result to display; its identity (via resultKey) drives the crossfade. */
  result?: InteractiveImageCreationResult;
  resultKey: number;
  resultLoaded: boolean;
  reduceMotion: boolean;
  /** Link for the gated-error sign-up CTA; falls back to a button. */
  signUpHref?: string;
  /** A generation failure to surface in the inset card (live mode only). */
  generationError?: InteractiveImageGenerationError;
  /** Fires once a result image has loaded, with the key that loaded so a late load from a
   *  superseded result can't mark a newer one ready. */
  onResultLoad: (loadedKey: number) => void;
  /** Retry the last generation (the "Try again" affordance on a non-gated error). */
  onRetry?: () => void;
}

export function CreateMode({
  createState,
  cardScale,
  contentScale,
  imageScale,
  hasResult,
  result,
  resultKey,
  resultLoaded,
  reduceMotion,
  signUpHref,
  generationError,
  onResultLoad,
  onRetry,
}: CreateModeProps) {
  const t = useTranslations("ClientKeys.InteractiveImage");

  const isGenerating = createState === "generating";
  const isResultLoading = createState === "result" && hasResult && !resultLoaded && !generationError;
  // Placeholder shows the empty hint before generation, then keeps the dotted loading surface
  // until the result image has actually loaded.
  const showPlaceholder = createState !== "result" || isResultLoading;
  const showGenerating = isGenerating || isResultLoading;

  const crossfade = makeCrossfade(reduceMotion);
  const cardStyle = { ...FULL_INSET, scale: cardScale };

  // Announced to screen readers: most relevant of error / generating / ready.
  const liveStatus = generationError
    ? generationError.message
    : showGenerating
      ? t("generating")
      : createState === "result"
        ? t("result_ready")
        : "";

  return (
    <>
      {/* Polite live region kept mounted across every state, since a region inserted at the moment
          of change is often skipped by screen readers. */}
      <div role='status' aria-live='polite' aria-atomic='true' className='tw-sr-only'>
        {liveStatus}
      </div>

      <AnimatePresence>
        {showPlaceholder && (
          <m.div
            key='placeholder'
            className={PLACEHOLDER_CARD_CLASS}
            style={cardStyle}
            initial={{ opacity: hasResult ? 0 : 1 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={crossfade}
          >
            <AnimatePresence mode='wait'>
              {showGenerating ? (
                <m.div
                  key='generating'
                  className='tw-absolute tw-inset-0 tw-flex tw-items-center tw-justify-center'
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  exit={{ opacity: 0 }}
                  transition={crossfade}
                >
                  <AnimatedDotGrid scale={imageScale} reduceMotion={reduceMotion} />
                  <div className='tw-absolute tw-left-1/2 tw-top-1/2 tw-h-32 tw-w-96 -tw-translate-x-1/2 -tw-translate-y-1/2 tw-bg-gray-200 [-webkit-mask-image:radial-gradient(ellipse_at_center,black_0%,black_24%,rgb(0_0_0/0.55)_58%,transparent_94%)] [mask-image:radial-gradient(ellipse_at_center,black_0%,black_24%,rgb(0_0_0/0.55)_58%,transparent_94%)]' />
                  <m.div
                    className='tw-relative tw-flex tw-w-80 tw-max-w-[60%] tw-flex-col tw-items-center'
                    style={{ scale: contentScale }}
                  >
                    <p className='shimmer-text tw-py-1 tw-leading-normal tw-type-sm'>{t("generating")}</p>
                    <RandomFact />
                  </m.div>
                </m.div>
              ) : (
                <m.div
                  key='empty'
                  className='tw-absolute tw-inset-0 tw-flex tw-flex-col tw-items-center tw-justify-center tw-gap-4 tw-px-6 tw-text-center'
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  exit={{ opacity: 0 }}
                  transition={crossfade}
                >
                  <m.div
                    className='tw-flex tw-flex-col tw-items-center tw-justify-center tw-gap-4'
                    style={{ scale: contentScale }}
                  >
                    <span className='tw-flex tw-size-10 tw-items-center tw-justify-center tw-rounded-[0.625rem] tw-border-0.5 tw-border-black/10'>
                      <IconFeature className={clsx(iconStrokeFix, "tw-size-6 tw-text-black/60 [&_path]:tw-stroke-2")} />
                    </span>
                    <p className='tw-max-w-80 tw-text-balance tw-text-black/60 tw-type-sm'>{t("empty_hint")}</p>
                  </m.div>
                </m.div>
              )}
            </AnimatePresence>
          </m.div>
        )}
      </AnimatePresence>

      <AnimatePresence>
        {hasResult && result && (
          <m.div
            key='result'
            // The card scale is target-aimed by the shared create-layer logic: results grow
            // full-frame and generation returns to the inset card.
            className={CARD_CLASS}
            style={cardStyle}
            initial={{ opacity: 0 }}
            animate={{ opacity: isGenerating || !resultLoaded ? 0 : 1 }}
            exit={{ opacity: 0 }}
            transition={crossfade}
          >
            <AnimatePresence initial={false}>
              <m.div
                key={resultKey}
                className='tw-absolute tw-inset-0'
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                style={{ scale: imageScale }}
                transition={crossfade}
              >
                <Image
                  src={result.imageUrl}
                  alt={result.alt ?? ""}
                  fill
                  draggable={false}
                  className='tw-select-none tw-object-cover'
                  sizes='(max-width: 64rem) 100vw, 73.5rem'
                  placeholder={result.blurDataURL ? "blur" : "empty"}
                  blurDataURL={result.blurDataURL}
                  onLoad={() => onResultLoad(resultKey)}
                />
              </m.div>
            </AnimatePresence>
          </m.div>
        )}
      </AnimatePresence>

      {/* Generation failure (live mode): card at the inset rect covering whatever is beneath. A
          "gated" error (quota / rate-limit) shows the sign-up CTA; any other shows "Try again". */}
      <AnimatePresence>
        {generationError && (
          <m.div
            key='error'
            className={clsx(
              PLACEHOLDER_CARD_CLASS,
              "tw-z-10 tw-flex tw-flex-col tw-items-center tw-justify-center tw-gap-4 tw-px-6 tw-text-center",
            )}
            style={cardStyle}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={crossfade}
          >
            <m.div className='tw-flex tw-flex-col tw-items-center tw-gap-4' style={{ scale: contentScale }}>
              <p className='tw-max-w-72 tw-text-balance tw-text-black/60 tw-type-sm'>{generationError.message}</p>
              {generationError.kind === "gated" ? (
                <ElevatedButton href={generationError.ctaUrl ?? signUpHref}>{t("sign_up")}</ElevatedButton>
              ) : (
                <ElevatedButton onClick={onRetry}>{t("error_retry")}</ElevatedButton>
              )}
            </m.div>
          </m.div>
        )}
      </AnimatePresence>
    </>
  );
}
